/**
 * Shared route registry — single source of truth for all wouter path patterns
 * and their component mappings.
 *
 * Adding or renaming a route requires only a change here:
 *   1. Update / add the entry in ROUTE_DEFINITIONS.
 *   2. The two Switch blocks in Router() (App.tsx) rebuild automatically.
 *   3. The smoke tests (routes.smoke.test.ts) import ROUTE_DEFINITIONS directly
 *      so they stay in sync without any manual update.
 *
 * Scopes:
 *   'shared'    — rendered in both the unauthenticated and authenticated Switch.
 *   'auth-only' — rendered only when the user is authenticated.
 */

import type { ComponentType } from "react";

import { JoinLeaguePage } from "../pages/JoinLeaguePage";
import { HowItWorks } from "../pages/HowItWorks";
import { PrivacidadPage, TerminosPage, PrivacyPage, TermsPage } from "../pages/LegalPageWrappers";
import { DeleteAccountPage } from "../pages/DeleteAccountPage";
import { AdminPanel } from "../pages/AdminPanel";
import { UnauthRoot } from "../pages/UnauthRoot";
import { AuthRoot } from "../pages/AuthRoot";
import { ScheduleMatchPage } from "../pages/ScheduleMatchPage";
import { RecordMatchPage } from "../pages/RecordMatchPage";
import { CallupDetailPage } from "../pages/CallupDetailPage";
import { CreateCallupPage } from "../pages/CreateCallupPage";
import { EditCallupPage } from "../pages/EditCallupPage";
import { EditLeaguePage } from "../pages/EditLeaguePage";
import { EditMatchPage } from "../pages/EditMatchPage";
import { CreateLeagueWrapper } from "../pages/CreateLeagueWrapper";
import { EditProfilePage } from "../pages/EditProfilePage";
import { AchievementsPage } from "../pages/AchievementsPage";
import { LevelEvolutionPage } from "../pages/LevelEvolutionPage";
import { MatchSummaryPage } from "../pages/MatchSummaryPage";
import { NationalLeaguePage } from "../pages/NationalLeaguePage";

export type RouteScope = "shared" | "auth-only";

export type RouteDefinition = {
  key: string;
  path: string;
  scope: RouteScope;
  component: ComponentType<object>;
};

/**
 * All application routes in declaration order.
 * Wouter's <Switch> matches first-wins, so more-specific paths (e.g.
 * "/leagues/:id/callups/:callupId/edit") must appear before shorter siblings.
 * The catch-all "/" entry MUST remain last in this array.
 */
export const ROUTE_DEFINITIONS: RouteDefinition[] = [
  // -------------------------------------------------------------------------
  // Shared — rendered in both the unauthenticated and authenticated Switch
  // -------------------------------------------------------------------------
  { key: "joinLeague",   path: "/join/:inviteCode", scope: "shared",    component: JoinLeaguePage },
  { key: "comoFunciona", path: "/como-funciona",    scope: "shared",    component: HowItWorks },
  { key: "privacidad",      path: "/privacidad",        scope: "shared",    component: PrivacidadPage },
  { key: "terminos",        path: "/terminos",          scope: "shared",    component: TerminosPage },
  { key: "privacy",         path: "/privacy",           scope: "shared",    component: PrivacyPage },
  { key: "terms",           path: "/terms",             scope: "shared",    component: TermsPage },
  { key: "eliminarCuenta",  path: "/eliminar-cuenta",  scope: "shared",    component: DeleteAccountPage },
  { key: "admin",        path: "/admin",             scope: "shared",    component: AdminPanel },

  // -------------------------------------------------------------------------
  // Authenticated only
  // -------------------------------------------------------------------------
  { key: "agendarPartido",    path: "/agendar-partido",                          scope: "auth-only", component: ScheduleMatchPage },
  { key: "registrarPartido",  path: "/registrar-partido",                        scope: "auth-only", component: RecordMatchPage },
  { key: "editCallup",        path: "/leagues/:leagueId/callups/:callupId/edit", scope: "auth-only", component: EditCallupPage },
  { key: "callupDetail",      path: "/leagues/:leagueId/callups/:callupId",      scope: "auth-only", component: CallupDetailPage },
  { key: "crearConvocatoria", path: "/leagues/:leagueId/crear-convocatoria",     scope: "auth-only", component: CreateCallupPage },
  { key: "editLeague",        path: "/leagues/:id/edit",                         scope: "auth-only", component: EditLeaguePage },
  { key: "editarPartido",     path: "/editar-partido/:matchId",                  scope: "auth-only", component: EditMatchPage },
  { key: "crearLiga",         path: "/crear-liga",                               scope: "auth-only", component: CreateLeagueWrapper },
  { key: "editarPerfil",      path: "/editar-perfil",                            scope: "auth-only", component: EditProfilePage },
  { key: "logros",            path: "/logros",                                   scope: "auth-only", component: AchievementsPage },
  { key: "nivelEvolucion",    path: "/nivel-evolucion",                          scope: "auth-only", component: LevelEvolutionPage },
  { key: "resumenPartido",    path: "/resumen-partido/:matchId",                 scope: "auth-only", component: MatchSummaryPage },
  { key: "ligaNacional",      path: "/ligas/nacional",                           scope: "auth-only", component: NationalLeaguePage },

  // -------------------------------------------------------------------------
  // Catch-all root — MUST remain last so wouter Switch matches specific routes first
  // -------------------------------------------------------------------------
  { key: "root-unauth", path: "/", scope: "shared",    component: UnauthRoot },
  { key: "root-auth",   path: "/", scope: "auth-only", component: AuthRoot },
];

/**
 * Convenience path-constant map — use ROUTES.xxx when you need a typed path
 * string outside of the Switch (e.g. `setLocation(ROUTES.editarPerfil)`).
 */
export const ROUTES = Object.fromEntries(
  ROUTE_DEFINITIONS
    .filter((r, i, arr) => arr.findIndex((x) => x.key === r.key) === i)
    .map((r) => [r.key, r.path]),
) as Record<string, string>;
